import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { Loader2, RefreshCw, Database } from "lucide-react";
import { toast } from "sonner";
import SummaryCards from "./SummaryCards";
import CompanyRow from "./CompanyRow";
import BatchSizeSelector from "./BatchSizeSelector";

export default function DataMigration() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [batchSize, setBatchSize] = useState(10);
    const [showConfirmAll, setShowConfirmAll] = useState(false);
    const { call: fetchStatus, isLoading } = useBluelagoon({ method: "getBackfillStatus" });
    const { call: backfillAll, isLoading: backfillingAll } = useBluelagoon({ method: "triggerAllBackfill" });

    const loadStatus = useCallback(async () => {
        const res = await fetchStatus();
        if (res?.data) setData(res.data);
    }, [fetchStatus]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const handleBackfillAll = useCallback(async () => {
        setShowConfirmAll(false);
        const res = await backfillAll({ batchSize });
        if (res?.success) {
            toast.success(`${t("adminDataMigration.backfillSuccess")}: ${res.data.completed}/${res.data.total}`);
            loadStatus();
        } else {
            toast.error(res?.error || t("adminDataMigration.backfillFailed"));
        }
    }, [backfillAll, batchSize, loadStatus, t]);

    return (
        <div className="p-6 space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" />{t("adminDataMigration.title")}</CardTitle>
                        <CardDescription>{t("adminDataMigration.description")}</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <BatchSizeSelector value={batchSize} onChange={setBatchSize} />
                        <Button variant="ghost" size="icon" onClick={loadStatus} disabled={isLoading}>
                            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button variant="default" disabled={backfillingAll || !data?.summary?.pending} onClick={() => setShowConfirmAll(true)}>
                            {backfillingAll && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {t("adminDataMigration.backfillAll")}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <SummaryCards summary={data?.summary} />
                    {data?.companies?.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>ID</TableHead>
                                    <TableHead>{t("adminDataMigration.company")}</TableHead>
                                    <TableHead>{t("adminDataMigration.status")}</TableHead>
                                    <TableHead className="text-center">{t("adminDataMigration.records")}</TableHead>
                                    <TableHead className="text-center">{t("adminDataMigration.databases")}</TableHead>
                                    <TableHead>{t("adminDataMigration.actions")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.companies.map((c) => (
                                    <CompanyRow key={c.companyId} company={c} onComplete={loadStatus} />
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="text-muted-foreground text-center py-8">{t("adminDataMigration.noCompanies")}</p>
                    )}
                </CardContent>
            </Card>
            <AlertDialog open={showConfirmAll} onOpenChange={setShowConfirmAll}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("adminDataMigration.backfillAll")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("adminDataMigration.backfillAllConfirm", { count: batchSize })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleBackfillAll}>{t("adminDataMigration.backfillAll")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
